import { useEffect, useRef, useState } from 'react';
import { Check, Info, Pause, Play, RotateCcw, Timer } from 'lucide-react';
import {
  STS_REFERENCE_CHAIR_HEIGHT_CM,
  normalizeFiveSTSPayload,
  type FiveSTSResultPayload,
} from '../sts/stsDomain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

type STSPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface FiveSTSTestProps {
  initial?: FiveSTSResultPayload | null;
  patientContext?: STSPatientContext;
  onCancel?: () => void;
  onNext: (result: FiveSTSResultPayload) => void;
}

export type { FiveSTSResultPayload };

export default function FiveSTSTest({ initial = null, patientContext, onCancel, onNext }: FiveSTSTestProps) {
  const normalizedInitial = normalizeFiveSTSPayload(initial);
  const initialWeightKg = normalizePatientWeightKg(normalizedInitial?.patientContext?.weightKg ?? patientContext?.weightKg);
  const [chairHeightCm, setChairHeightCm] = useState<number>(
    normalizedInitial?.chairHeightCm ?? STS_REFERENCE_CHAIR_HEIGHT_CM,
  );
  const [usedArms, setUsedArms] = useState(Boolean(normalizedInitial?.usedArms));
  const [attempt, setAttempt] = useState<1 | 2>((normalizedInitial?.attempts.length ?? 0) === 0 ? 1 : 2);
  const [times, setTimes] = useState<number[]>([
    normalizedInitial?.attempts[0] ?? 0,
    normalizedInitial?.attempts[1] ?? 0,
  ]);
  const [weightKg, setWeightKg] = useState<number | null>(initialWeightKg);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [cooldown, setCooldown] = useState(0);

  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const cooldownRef = useRef<number | null>(null);
  useEffect(() => {
    if (!running) {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    startRef.current = performance.now() - elapsed * 1000;
    const tick = () => {
      const now = performance.now();
      const delta = now - (startRef.current ?? now);
      setElapsed(delta / 1000);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [elapsed, running]);

  useEffect(() => {
    if (cooldown <= 0) {
      if (cooldownRef.current != null) {
        window.clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
      return;
    }

    cooldownRef.current = window.setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000) as unknown as number;
    return () => {
      if (cooldownRef.current != null) {
        window.clearInterval(cooldownRef.current);
        cooldownRef.current = null;
      }
    };
  }, [cooldown]);

  const handleStart = () => setRunning(true);
  const handlePause = () => setRunning(false);
  const handleReset = () => {
    setRunning(false);
    setElapsed(0);
  };

  const finishAttempt = () => {
    const currentIndex = attempt === 1 ? 0 : 1;
    const nextValue = Math.max(0.01, Number(elapsed.toFixed(2)));
    setTimes((current) => current.map((value, index) => (index === currentIndex ? nextValue : value)));
    setRunning(false);
    setElapsed(0);

    if (attempt === 1) {
      setAttempt(2);
      setCooldown(60);
    }
  };

  const best = [times[0] || Number.POSITIVE_INFINITY, times[1] || Number.POSITIVE_INFINITY].reduce((min, value) => Math.min(min, value), Number.POSITIVE_INFINITY);
  const bestOrNull = best === Number.POSITIVE_INFINITY ? null : best;
  const effectivePatientContext = {
    ...patientContext,
    ageYears: toFiniteNumber(patientContext?.ageYears),
    weightKg,
    heightCm: normalizePatientHeightCm(patientContext?.heightCm),
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">Sentar-Levantar 5x (5xSTS)</p>
          <p className="text-sm opacity-90">
            Use cadeira sem braços e estável. Para comparação normativa automática,
            utilize {STS_REFERENCE_CHAIR_HEIGHT_CM} cm; outras alturas permanecem
            registradas, mas sem comparação normativa automática. Cronometre do "já" até sentar após a
            5ª repetição. São duas tentativas e o melhor tempo entra no laudo.
          </p>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm">
          Altura da cadeira (cm)
          <input
            type="number"
            className="mt-1 w-full rounded border px-2 py-1"
            value={chairHeightCm}
            onChange={(event) =>
              setChairHeightCm(
                Number(event.target.value) || STS_REFERENCE_CHAIR_HEIGHT_CM,
              )
            }
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={usedArms} onChange={(event) => setUsedArms(event.target.checked)} />
          Usou os braços em alguma repetição
        </label>
        <div className="text-sm">
          Tentativa atual: <span className="font-semibold">{attempt}ª</span>
        </div>
      </div>

      <AssessmentWeightAdjustmentField
        value={weightKg}
        baselineValue={initialWeightKg}
        onChange={setWeightKg}
        helperText="Use quando o peso aferido nesta avaliação estiver diferente do cadastro. O ajuste acompanha apenas este 5xSTS."
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="text-3xl tabular-nums">{elapsed.toFixed(2)}s</div>
        {!running ? (
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-white disabled:opacity-50"
            onClick={handleStart}
            disabled={attempt === 2 && cooldown > 0}
          >
            <Play className="mr-1 h-4 w-4" /> Iniciar
          </button>
        ) : (
          <button type="button" className="inline-flex items-center rounded-md bg-amber-600 px-3 py-2 text-white" onClick={handlePause}>
            <Pause className="mr-1 h-4 w-4" /> Pausar
          </button>
        )}
        <button type="button" className="inline-flex items-center rounded-md bg-gray-100 px-3 py-2" onClick={handleReset}>
          <RotateCcw className="mr-1 h-4 w-4" /> Zerar
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-white disabled:opacity-50"
          onClick={finishAttempt}
          disabled={elapsed === 0}
        >
          Concluir tentativa <Check className="ml-1 h-4 w-4" />
        </button>
        {attempt === 2 && cooldown > 0 ? (
          <div className="ml-auto inline-flex items-center gap-1 text-sm text-gray-700">
            <Timer className="h-4 w-4" /> Descanso: {cooldown}s
          </div>
        ) : null}
      </div>

      <div className="grid gap-3 text-sm md:grid-cols-3">
        <div>
          Tempo 1ª: <span className="font-semibold">{times[0] ? `${times[0].toFixed(2)}s` : '—'}</span>
        </div>
        <div>
          Tempo 2ª: <span className="font-semibold">{times[1] ? `${times[1].toFixed(2)}s` : '—'}</span>
        </div>
        <div>
          Melhor tempo: <span className="font-semibold">{bestOrNull != null ? `${bestOrNull.toFixed(2)}s` : '—'}</span>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>
        <button
          type="button"
          className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-2 text-white disabled:opacity-50"
          disabled={bestOrNull == null}
          onClick={() =>
            onNext({
              attempts: times.filter((value) => value > 0),
              best: bestOrNull,
              chairHeightCm,
              usedArms,
              workflowStatus: 'completed',
              finishedAt: new Date().toISOString(),
              patientContext: effectivePatientContext,
            })
          }
        >
          Salvar 5xSTS <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
